import {t, chooseLanguage, LANGUAGES} from './i18n.mjs';
import {icon} from './icons.mjs';
// Language choice with flags. A native <select> cannot show images, so this is a disclosure button
// plus an inline listbox (WAI-ARIA listbox: arrows/Home/End move focus, Enter/Space/click select,
// Escape closes). Flags are SVG files from vendor/flag-icons (MIT); emoji flags show as "DE" on Windows.
export const CHOICES = ['auto', ...LANGUAGES];
export const FLAG_FILES = {de: 'de', en: 'gb', fr: 'fr', es: 'es', it: 'it', nl: 'nl', pl: 'pl', pt: 'pt', sv: 'se', cs: 'cz'};
const NAME_KEYS = {de: 'languageDe', en: 'languageEn', fr: 'languageFr', es: 'languageEs', it: 'languageIt', nl: 'languageNl', pl: 'languagePl', pt: 'languagePt', sv: 'languageSv', cs: 'languageCs'};
export const languageName = lang => t(NAME_KEYS[lang]);
export const flag = lang => `<img class="flag" src="/web/vendor/flag-icons/${FLAG_FILES[lang]}.svg" alt="" width="20" height="15" decoding="async">`;
// Auto shows the detected language in its label ("Automatic (Deutsch)") next to a globe; only the
// concrete languages carry a flag, so a flag always means "this language was chosen".
export const choiceLabel = (choice, detected) => choice === 'auto' ? t('autoDetected', {language: languageName(detected)}) : languageName(choice);
export const choiceVisual = (choice, detected) => choice === 'auto'
  ? `<span class="flag-auto">${icon('globe')}</span>` : flag(choice);
export function choiceModel(selected, detected) {
  return CHOICES.map(value => ({value, label: choiceLabel(value, detected), visual: choiceVisual(value, detected), selected: value === selected}));
}
// Roving focus inside the list.
export function nextIndex(key, index, count) {
  if (key === 'ArrowDown') return Math.min(count - 1, index + 1);
  if (key === 'ArrowUp') return Math.max(0, index - 1);
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return index;
}

export function pickerMarkup() {
  return `<label id="language-heading" class="language-choice" for="language-button" data-i18n="language">${t('language')}</label>
<button id="language-button" class="language-button" type="button" aria-haspopup="listbox" aria-expanded="false" aria-controls="language-list" aria-labelledby="language-heading language-current">
<span class="language-visual" aria-hidden="true"></span><span id="language-current" class="language-name"></span><span class="language-chevron" aria-hidden="true">${icon('chevron')}</span></button>
<ul id="language-list" class="language-list" role="listbox" aria-labelledby="language-heading" hidden></ul>`;
}

export function languagePicker({value = 'auto', detected = () => chooseLanguage('auto', globalThis.navigator?.languages || []), onChange = () => {}} = {}) {
  const $ = id => document.getElementById(id);
  const button = $('language-button'), list = $('language-list');
  let selected = value;
  const options = () => [...list.querySelectorAll('[role="option"]')];
  function render(next = selected) {
    selected = next;
    const found = detected(), model = choiceModel(selected, found), active = model.find(m => m.selected);
    button.querySelector('.language-visual').innerHTML = active.visual;
    $('language-current').textContent = active.label;
    list.innerHTML = model.map(m => `<li id="language-option-${m.value}" role="option" class="language-option" data-value="${m.value}" aria-selected="${m.selected}" tabindex="${m.selected ? 0 : -1}"><span class="language-visual" aria-hidden="true">${m.visual}</span><span class="language-name">${m.label}</span><span class="language-check" aria-hidden="true">${icon('check')}</span></li>`).join('');
  }
  function open(show) {
    list.hidden = !show; button.setAttribute('aria-expanded', String(show));
    if (show) (options().find(o => o.getAttribute('aria-selected') === 'true') || options()[0])?.focus();
  }
  function choose(next) {
    open(false); button.focus();
    if (next === selected) return;
    render(next); onChange(next);
  }
  button.onclick = () => open(list.hidden);
  list.addEventListener('click', event => { const o = event.target.closest('[role="option"]'); if (o) choose(o.dataset.value); });
  list.addEventListener('keydown', event => {
    const all = options(), index = all.indexOf(document.activeElement);
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); open(false); button.focus(); return; }
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); if (index >= 0) choose(all[index].dataset.value); return; }
    const next = nextIndex(event.key, index, all.length);
    if (next !== index) { event.preventDefault(); all.forEach((o, i) => o.tabIndex = i === next ? 0 : -1); all[next].focus(); }
  });
  render();
  return {render, open, get value() { return selected; }};
}
